import { Badge, EmptyState, ErrorState, Loading, Notice, PageHeader } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSession } from '@/app/session';
import type { Json } from '@/lib/database.types';
import { errorText } from '@/lib/errors';
import { supabase } from '@/lib/supabase';
import './settings.css';

/**
 * One event as public.audit_log serves it (ADR-024). The server decides what each reader sees:
 * shift and cash events reach a reader without shift.review (the owner's) as metadata only
 * (`redacted`: who, what, which terminal and shift, when), never a figure or the text typed with
 * it. The page shows what it gets and has no redaction of its own. The generated types call every
 * column non-null; most of them may be null.
 */
interface AuditRow {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  actor_employee_id: string | null;
  approver_employee_id: string | null;
  terminal_id: string | null;
  shift_id: string | null;
  reason: string | null;
  amount_minor: number | null;
  occurred_at: string;
  recorded_at: string;
  command_id: string | null;
  before: Json | null;
  after: Json | null;
  redacted: boolean;
}

/** What an opened row shows: the fields the server sent (a redacted row has no figures to send). */
function detailOf(r: AuditRow) {
  return Object.fromEntries(
    Object.entries({
      before: r.before,
      after: r.after,
      amount_minor: r.amount_minor,
      shift_id: r.shift_id,
      terminal_id: r.terminal_id,
      command_id: r.command_id,
      occurred_at: r.occurred_at,
    }).filter(([, v]) => v !== null && v !== undefined),
  );
}

export function AuditPage() {
  const s = useSession();
  const owner = s.can('shift.review');
  const [open, setOpen] = useState<string | null>(null);
  const rows = useQuery({
    queryKey: ['audit'],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('audit_log', { p_limit: 200 });
      if (error) throw error;
      return (data ?? []) as AuditRow[];
    },
  });
  const employees = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const { data, error } = await supabase.from('employee').select('id, display_name');
      if (error) throw error;
      return data;
    },
  });
  const name = (id: string | null) =>
    id ? (employees.data?.find((e) => e.id === id)?.display_name ?? id.slice(0, 8)) : 'system';
  const toggle = (id: string) => setOpen(open === id ? null : id);
  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Settings"
        title="Audit log"
        subtitle="Every recorded change, newest first (the latest 200). Nothing here can be edited or removed."
      />
      {!owner && (
        <Notice tone="info" testId="audit-owner-figures">
          Shift and cash events show who did what, where and when. Their amounts and counts are the
          owner&apos;s.
        </Notice>
      )}
      {rows.error && (
        <ErrorState
          title="Could not load the audit log"
          message={errorText(rows.error)}
          onRetry={() => void rows.refetch()}
          testId="audit-error"
        />
      )}
      {rows.isLoading && <Loading rows={6} label="Loading the audit log…" />}
      {rows.data && rows.data.length > 0 && (
        <div className="d-table-scroll audit__scroll">
          <table className="d-table" data-testid="audit-table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Action</th>
                <th scope="col">Entity</th>
                <th scope="col">Actor</th>
                <th scope="col">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.data.map((r) => (
                <tr
                  key={r.id}
                  className="d-table__row--button"
                  tabIndex={0}
                  aria-expanded={open === r.id}
                  onClick={() => toggle(r.id)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(r.id);
                    }
                  }}
                  data-testid="audit-row"
                >
                  <td className="d-mono audit__when">{new Date(r.recorded_at).toLocaleString()}</td>
                  <td>
                    <div className="audit__action">
                      <code className="d-mono audit__code">{r.action}</code>
                      {r.redacted && (
                        <Badge tone="info" testId="audit-redacted">
                          Figures: owner only
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="audit__entity">
                    {r.entity_type}
                    {open === r.id && (
                      // selecting text in the detail does not close the row
                      <pre
                        className="d-mono audit__detail"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="audit-detail"
                      >
                        {JSON.stringify(detailOf(r), null, 2)}
                      </pre>
                    )}
                  </td>
                  <td className="d-break">
                    {name(r.actor_employee_id)}
                    {r.approver_employee_id && (
                      <span className="d-faint"> · approved by {name(r.approver_employee_id)}</span>
                    )}
                  </td>
                  <td className="d-muted audit__reason">{r.reason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.data?.length === 0 && <EmptyState title="No events yet" />}
    </div>
  );
}
